/**
 * Flowlytics Pixel SDK - Initialization
 * Main entry point for SDK initialization.
 *
 * Payload strategy:
 *   - page_view (first event): sends FULL data (cookies_decoded, device, fingerprint, session)
 *   - subsequent events (custom, GTM): sends LIGHTWEIGHT data (identity IDs, fingerprint hash, session)
 *   This reduces payload from ~10-15KB to ~1-2KB for non-page_view events.
 */

import FlowlyticsConfig from "./config";
import EventQueue from "./queue";
import CookielessTracker from "./cookieless";
import DeviceCollector from "../collectors/device";
import SessionCollector from "../collectors/session";
import FirstPartyCollector from "../collectors/first-party";
import GTMIntegration from "../integrations/gtm";
import ConsentIntegration from "../integrations/consent";
import ConversionBridge from "../integrations/conversion-bridge";
import DiscoveryIntegration from "../integrations/discovery";

class Flowlytics {
  constructor(options = {}) {
    // Initialize configuration
    this.config = new FlowlyticsConfig(options);

    // Initialize event queue
    this.queue = new EventQueue(this.config);

    // Initialize collectors (cookie + fingerprint lazy-loaded for smaller main bundle)
    this.collectors = {
      cookie: null, // S7.3c: Lazy-loaded on first page_view (~20KB saved)
      device: new DeviceCollector(this.config),
      session: new SessionCollector(this.config),
      fingerprint: null, // S7.3c: Lazy-loaded on first page_view (~5KB saved)
      firstParty: new FirstPartyCollector(this.config),
    };

    // Cookieless tracking fallback
    this.cookieless = new CookielessTracker(this.config);

    // Initialize integrations
    this.integrations = {
      gtm: null,
      consent: null,
      conversionBridge: null,
      discovery: null,
    };

    // SDK state
    this.initialized = false;
    this.sessionId = null;
    this.userId = null;

    // Cached data (collected once, reused for all events)
    this._cachedDevice = null;
    this._cachedFingerprint = null;
    this._cachedCrossPlatformIds = null; // S7.3c: Cached after first page_view
    this._fullDataSent = false; // Track if full payload was sent this session

    // S7: Pre-consent event queue (events queued before consent decision)
    this._preConsentQueue = [];
    this._consentResolved = false;

    if (this.config.debug) {
      console.log("[Flowlytics] SDK initialized with config:", this.config);
    }
  }

  /**
   * Initialize SDK and start tracking
   */
  async init() {
    if (this.initialized) {
      console.warn("[Flowlytics] SDK already initialized");
      return;
    }

    try {
      // Initialize cookieless tracker (detect cookie availability)
      this.cookieless.init();

      // Initialize session
      this.sessionId = this.collectors.session.getOrCreateSession();

      // Setup Consent integration FIRST (before any tracking)
      if (this.config.features.consentMode) {
        this.integrations.consent = new ConsentIntegration(this.config, this);
        this.integrations.consent.init();
      }

      // S7.3d: Show consent banner if consent not yet decided
      // Never next to Iubenda: the site's CMP is the record of the visitor's
      // decision, and a second banner could grant measurement Iubenda never
      // gave. Measured 2026-09-10: the banner chunk loaded 17,941 times in 15
      // days, almost all on Iubenda sites. Checked again in _showConsentBanner.
      if (
        this.config.consentBanner !== false &&
        this.integrations.consent &&
        !this.integrations.consent.hasDecided() &&
        !this.integrations.consent.isIubendaPresent()
      ) {
        this._showConsentBanner();
      }

      // Defer page_view until consent decision when ANY consent manager is active.
      // _flushPreConsentQueue() re-fires trackPageView once consent is decided.
      const consentBlocking =
        this.integrations.consent && !this.integrations.consent.hasDecided();

      if (consentBlocking) {
        if (this.config.debug) {
          console.log(
            "[Flowlytics] Consent pending, deferring page_view until consent decision",
          );
        }
      } else {
        await this.trackPageView();
      }

      // Setup GTM integration
      if (this.config.features.gtmIntegration) {
        this.integrations.gtm = new GTMIntegration(this.config, this);
        this.integrations.gtm.init();

        // Connect consent to GTM for dataLayer consent detection
        if (this.integrations.consent) {
          this.integrations.gtm.setConsentIntegration(
            this.integrations.consent,
          );
        }
      }

      // Discovery: static snapshot of analytics tools + pre-existing dataLayer events.
      // Runs async (setTimeout 0) to avoid delaying page_view.
      this.integrations.discovery = new DiscoveryIntegration(this.config);
      this.integrations.discovery.init();

      // Fetch remote config (conversions, features) from backend if not provided locally
      if (!this.config.get("conversions")) {
        await this._fetchRemoteConfig();
      }

      // Setup Conversion Bridge (client-side conversion tracking to ad platforms)
      if (this.config.get("conversions")) {
        this.integrations.conversionBridge = new ConversionBridge(this.config);
        this.integrations.conversionBridge.init();
      }

      // Setup first-party data collection (form capture)
      this.collectors.firstParty.setupFormCapture((formData) => {
        this.track("form_capture", {
          ...formData,
          event_source: "first_party_auto",
        });
      });

      // Request server-set cookie if third-party cookies are blocked
      if (!this.cookieless.areCookiesAvailable()) {
        this.cookieless.requestServerCookie();
      }

      // Setup page unload handler
      this.setupUnloadHandler();

      // Setup SPA route change detection (History API + hashchange)
      this.setupSPATracking();

      this.initialized = true;

      if (this.config.debug) {
        console.log("[Flowlytics] SDK ready. Session ID:", this.sessionId);
      }
    } catch (error) {
      console.error("[Flowlytics] Initialization failed:", error);
    }
  }

  /**
   * Fetch remote config (conversions, features) from backend.
   * Allows the SDK to load ConversionBridge tags without hardcoding
   * platform IDs in the client snippet — config comes from the pixel installation DB.
   */
  async _fetchRemoteConfig() {
    try {
      const apiKey = this.config.apiKey;
      // Derive Flowlitycs server URL from the SDK script src (works cross-domain)
      let baseUrl = "";
      try {
        const scripts = document.querySelectorAll('script[src*="flowlytics"]');
        for (const s of scripts) {
          if (s.src.includes("/sdk/")) {
            baseUrl = new URL(s.src).origin;
            break;
          }
        }
      } catch (_) {
        /* fallback below */
      }
      // Fallback: derive from endpoint config
      if (!baseUrl) {
        const endpoint = this.config.endpoint || "";
        baseUrl = endpoint.startsWith("http")
          ? endpoint.substring(0, endpoint.indexOf("/api/"))
          : "";
      }
      const configUrl = `${baseUrl}/api/pixel/config/${apiKey}`;

      const response = await fetch(configUrl, { cache: "default" });
      if (!response.ok) return;

      const result = await response.json();
      if (result.success && result.data) {
        if (result.data.conversions) {
          this.config.set("conversions", result.data.conversions);
        }
        if (this.config.debug) {
          console.log(
            "[Flowlytics] Remote config loaded:",
            Object.keys(result.data).filter((k) => result.data[k]),
          );
        }
      }
    } catch (_) {
      // Non-blocking — ConversionBridge simply won't init if config unavailable
    }
  }

  /**
   * Collect FULL tracking data with decoded cookies and behavioral signals.
   * Used for page_view events (first event of session).
   * Device and fingerprint data are collected ONCE and cached for the session.
   */
  async collectData() {
    const data = {
      session_id: this.sessionId,
      user_id: this.userId,
      timestamp: new Date().toISOString(),
    };

    // S7.3c: Lazy-load CookieCollector on first use (~20KB code-split chunk)
    // Cookie reading requires analytics consent — first-party tracking works without it
    if (this.config.features.cookieTracking && this._isCookieConsentGranted()) {
      if (!this.collectors.cookie) {
        const { default: CookieCollector } = await import(
          /* webpackChunkName: "cookies" */ "../collectors/cookies"
        );
        this.collectors.cookie = new CookieCollector(this.config);
      }
      data.cookies_decoded = this.collectors.cookie.collectDecoded();
      data.cookies = this.collectors.cookie.collect();
      this._cachedCrossPlatformIds =
        this.collectors.cookie.getCrossPlatformIds();
    }

    // Collect device info and fingerprint — gated on analytics consent when a
    // CMP (e.g. Iubenda) is present, to avoid fingerprinting before consent.
    if (
      this.config.features.deviceFingerprint &&
      this._isCookieConsentGranted()
    ) {
      if (!this._cachedDevice) {
        const rawDevice = this.collectors.device.collect();
        const summary = this.collectors.device.getSummary
          ? this.collectors.device.getSummary()
          : {};
        this._cachedDevice = { ...rawDevice, ...summary };
      }
      data.device = this._cachedDevice;

      // S7.3c: Lazy-load FingerprintCollector on first use (~5KB code-split chunk)
      if (!this._cachedFingerprint) {
        if (!this.collectors.fingerprint) {
          const { default: FingerprintCollector } = await import(
            /* webpackChunkName: "fingerprint" */ "../collectors/fingerprint"
          );
          this.collectors.fingerprint = new FingerprintCollector(this.config);
        }
        this._cachedFingerprint = await this.collectors.fingerprint.generate();
      }
      data.fingerprint = this._cachedFingerprint;
    }

    // Collect session info
    if (this.config.features.sessionTracking) {
      data.session = this.collectors.session.collect();
    }

    // Collect first-party data (click IDs, UTMs, stored identity)
    data.first_party = this.collectors.firstParty.collect();

    // Cookieless identity fallback — getIdentity() uses the fingerprint as the
    // client id when nothing else is available, so it is gated like the rest.
    data.cookieless_identity = this.cookieless.getIdentity(
      this._isCookieConsentGranted()
        ? this._cachedFingerprint?.fingerprint
        : undefined,
    );

    return data;
  }

  /**
   * Collect LIGHTWEIGHT tracking data for non-page_view events.
   * Sends only identity IDs + fingerprint hash + session info.
   * Full cookies/device/fingerprint were already sent with page_view.
   *
   * The caches below were filled under the consent state of THAT page_view.
   * Consent is checked again here, at the point of use: a cache that outlives a
   * denial must not keep going out (see ConsentIntegration._forgetAnalyticsDataIfDenied).
   */
  async collectLightData() {
    const data = {
      session_id: this.sessionId,
      user_id: this.userId,
      timestamp: new Date().toISOString(),
      _lightweight: true, // Flag for backend to know this is a light event
    };

    const analyticsGranted = this._isCookieConsentGranted();

    // S7.3c: Use cached cross-platform IDs (populated on page_view, avoids loading heavy cookie module)
    if (this.config.features.cookieTracking) {
      data.identity = analyticsGranted
        ? this._cachedCrossPlatformIds || {}
        : {};
    }

    // Only send fingerprint hash (not full components)
    if (
      this.config.features.deviceFingerprint &&
      analyticsGranted &&
      this._cachedFingerprint
    ) {
      data.fingerprint = {
        fingerprint: this._cachedFingerprint.fingerprint,
        confidence: this._cachedFingerprint.confidence,
      };
    }

    // Session info (lightweight)
    if (this.config.features.sessionTracking) {
      data.session = this.collectors.session.collect();
    }

    return data;
  }

  /**
   * Forget everything collected under analytics consent (fingerprint, device,
   * cookie ids). Called by ConsentIntegration when a decision denies analytics;
   * a later granted page_view collects them again from scratch.
   */
  _dropAnalyticsGatedCaches() {
    this._cachedFingerprint = null;
    this._cachedDevice = null;
    this._cachedCrossPlatformIds = null;
  }

  /**
   * Check if tracking is allowed based on consent state.
   * First-party server-side tracking always passes (legitimate interest).
   * Consent only gates cookie collection and CAPI/ad-pixel forwarding.
   */
  _isTrackingAllowed(_eventName) {
    // First-party analytics always allowed — consent state is included in the
    // payload so the backend can decide what to forward via CAPI.
    return true;
  }

  /**
   * Whether third-party cookie reading is consented.
   * Used by collectData() to gate cookie collection.
   */
  _isCookieConsentGranted() {
    const consent = this.integrations.consent;
    if (!consent) return true; // No consent integration = allow all
    if (!consent.hasDecided()) return false;
    return consent.isAnalyticsAllowed();
  }

  /**
   * Track page view - sends FULL payload
   */
  async trackPageView(additionalData = {}) {
    // First-party tracking always fires — consent only gates cookie collection
    const data = await this.collectData();

    // S7: Include consent state in every event payload for server-side validation
    const consentData = this.integrations.consent
      ? {
          consent_state: this.integrations.consent.getGDPRConsent(),
          consent_decided: this.integrations.consent.hasDecided(),
        }
      : {};

    // Collect behavioral profile only when analytics consent is granted (ePrivacy)
    const behaviorProfile = this._isCookieConsentGranted()
      ? this.collectors.firstParty.collectBehaviorProfile()
      : null;

    this.queue.add({
      event_type: "page_view",
      event_source: "flowlytics_pixel",
      page_url: window.location.href,
      page_title: document.title,
      referrer: document.referrer,
      ...data,
      ...consentData,
      ...additionalData,
      behavior_profile: behaviorProfile,
    });

    this._fullDataSent = true;
  }

  /**
   * Track custom event - sends LIGHTWEIGHT payload
   * (full data already sent with page_view)
   */
  async track(eventName, properties = {}) {
    // First-party tracking always fires — consent only gates cookie collection and CAPI

    // Use lightweight data if full data was already sent, otherwise full
    const data = this._fullDataSent
      ? await this.collectLightData()
      : await this.collectData();

    // S7: Include consent state in every event payload for server-side validation
    const consentData = this.integrations.consent
      ? {
          consent_state: this.integrations.consent.getGDPRConsent(),
          consent_decided: this.integrations.consent.hasDecided(),
        }
      : {};

    // Attach persisted click IDs for identity resolution and attribution
    const clickIds = this.integrations.conversionBridge
      ? this.integrations.conversionBridge.getClickIds()
      : {};

    // Generated here (not in queue.add) so the SAME id reaches both the
    // Flowlytics backend (→ CAPI event_id) and the browser pixels (fbq
    // eventID): Meta dedupes browser vs server events only on a shared id.
    const eventId = this.queue.generateEventId();

    this.queue.add({
      event_id: eventId,
      event_type: eventName,
      event_source: properties.event_source || "flowlytics_pixel",
      properties: properties,
      ...data,
      ...consentData,
      ...clickIds, // gclid, fbclid, li_fat_id, utm_* — persisted across pages
    });

    // Fire client-side conversion pixels only with marketing consent
    if (this.integrations.conversionBridge) {
      const consent = this.integrations.consent;
      const marketingAllowed = !consent || consent.isAdvertisingAllowed();
      if (marketingAllowed) {
        this.integrations.conversionBridge.onEvent(
          eventName,
          properties,
          eventId,
        );
      }
    }
  }

  /**
   * Called by ConsentIntegration after any consent decision (accept or reject).
   *
   * Two cases:
   * 1. Iubenda was blocking the initial page_view → fire it now (with or without
   *    cookies depending on what was granted).
   * 2. Page_view was already sent without cookies → re-send with cookies if analytics
   *    was just granted.
   */
  async _flushPreConsentQueue() {
    if (this._consentResolved) return;
    this._consentResolved = true;

    const consent = this.integrations.consent;
    const analyticsGranted = consent ? consent.isAnalyticsAllowed() : true;
    const marketingGranted = consent
      ? consent.gdprConsent?.marketing === true
      : true;

    // Persist localStorage data now that consent is known (ePrivacy compliance)
    this.cookieless.persistIfConsented(analyticsGranted);
    this.collectors.session.persistIfConsented(analyticsGranted);
    this.collectors.firstParty.persistIfConsented(marketingGranted);

    // Fire deferred page_view if not yet sent (any CMP blocked it, not just Iubenda)
    if (!this._fullDataSent) {
      if (this.config.debug) {
        console.log(
          "[Flowlytics] Consent resolved — firing deferred page_view",
          { analyticsGranted },
        );
      }
      await this.trackPageView({ _consent_resolved: true });
      return;
    }

    // Re-send with enriched cookie data if analytics now granted
    if (analyticsGranted && !this._cachedCrossPlatformIds) {
      if (this.config.debug) {
        console.log(
          "[Flowlytics] Analytics consent granted — re-sending page_view with cookie data",
        );
      }
      this._fullDataSent = false;
      await this.trackPageView({ _consent_enrichment: true });
      return;
    }

    if (this.config.debug) {
      console.log("[Flowlytics] Consent resolved, no further action needed");
    }
  }

  /**
   * Identify user
   */
  identify(userId, traits = {}) {
    this.userId = userId;

    this.queue.add({
      event_type: "identify",
      event_source: "flowlytics_pixel",
      user_id: userId,
      traits: traits,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Setup page unload handler to flush queue
   */
  setupUnloadHandler() {
    window.addEventListener("beforeunload", () => {
      this.queue.flush();
    });

    // Visibility change (page hidden)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.queue.flush();
      }
    });
  }

  /**
   * SPA route change detection — track page_view on History API navigation
   */
  setupSPATracking() {
    let lastUrl = window.location.href;

    const onRouteChange = () => {
      const newUrl = window.location.href;
      if (newUrl !== lastUrl) {
        lastUrl = newUrl;
        this.trackPageView();
      }
    };

    // Monkey-patch pushState and replaceState
    const originalPushState = history.pushState;
    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      onRouteChange();
    };

    const originalReplaceState = history.replaceState;
    history.replaceState = function (...args) {
      originalReplaceState.apply(this, args);
      onRouteChange();
    };

    // Listen for popstate (back/forward buttons)
    window.addEventListener("popstate", onRouteChange);

    // Listen for hash changes (hash-based routing)
    window.addEventListener("hashchange", onRouteChange);
  }

  /**
   * S7.3d: Lazy-load and show consent banner (Shadow DOM, style-isolated).
   */
  async _showConsentBanner() {
    try {
      // Iubenda can arrive after init (GTM injects both): look again before
      // downloading the banner, and once more after, in case it came meanwhile.
      if (this.integrations.consent?.isIubendaPresent()) return;
      const { default: ConsentBanner } = await import(
        /* webpackChunkName: "consent-banner" */ "../ui/consent-banner"
      );
      if (this.integrations.consent?.isIubendaPresent()) return;
      const bannerConfig =
        typeof this.config.consentBanner === "object"
          ? this.config.consentBanner
          : {};
      const banner = new ConsentBanner({
        mode: bannerConfig.mode || "simple",
        theme: bannerConfig.theme || "dark",
        position: bannerConfig.position || "bottom",
        labels: bannerConfig.labels || {},
        privacyPolicyUrl: bannerConfig.privacyPolicyUrl || null,
        // No onConsent callback — consent.js already listens for the
        // 'flowlytics_gdpr_consent' window event dispatched by the banner.
        // Having both caused every click to fire consent_update twice.
      });
      banner.show();
    } catch (err) {
      if (this.config.debug) {
        console.warn(
          "[Flowlytics] Failed to load consent banner:",
          err.message,
        );
      }
    }
  }

  /**
   * Get SDK version
   */
  getVersion() {
    return __SDK_VERSION__;
  }

  /**
   * Debug method to inspect collected data
   */
  async debug() {
    if (!this.config.debug) {
      console.warn(
        "[Flowlytics] Debug mode is disabled. Enable with debug: true",
      );
      return;
    }

    const data = await this.collectData();
    console.log("[Flowlytics Debug] Collected Data:", data);
    console.log("[Flowlytics Debug] Queue Size:", this.queue.size());
    return data;
  }
}

export default Flowlytics;
